/**
 * Quem NÃO passa pelo envelope do compilador — e por isso nunca tem contexto
 * guardado (spec 2026-09-20 §5.6).
 *
 * São dois casos, e só dois:
 *  - o nó está pendurado no ponto "tools" de um agente em linha: ele vira uma
 *    ferramenta do agente, chamada pelo modelo, fora do caminho normal do
 *    fluxo. O ponto "tools" vale nas DUAS pontas da ligação, porque é assim
 *    que o compilador conta (`collectInlineToolEdges` em
 *    `graph-validator.ts`: `edge.sourceHandle === 'tools' || edge.targetHandle
 *    === 'tools'`, e a ferramenta é a ponta que não é o agente);
 *  - o nó É o "Tentar de novo" (o escopo em si, não o que está dentro dele).
 *
 * Os nós DENTRO do corpo do "Tentar de novo" passam pelo envelope e gravam
 * normalmente — por isso aqui não existe travessia de grafo: uma ligação e um
 * tipo, nada mais.
 */

/** Tipo do nó "Tentar de novo". */
const RETRY_SCOPE_NODE_TYPE = 'control_retry_scope';
/** Ponto pelo qual o agente em linha pendura suas ferramentas. */
const AGENT_TOOL_HANDLE = 'tools';

export type UnrecordedNodeReason = 'agent_tool' | 'retry_scope';

/** Formato mínimo de nó que esta conta precisa (compatível com o do React Flow). */
export interface NodeEnvelopeNode {
  id: string;
  data?: { type?: string } | undefined;
}

/** Formato mínimo de ligação que esta conta precisa (compatível com o do React Flow). */
export interface NodeEnvelopeEdge {
  source?: string | undefined;
  target: string;
  sourceHandle?: string | null | undefined;
  targetHandle?: string | null | undefined;
}

export function getUnrecordedNodeReason(
  node: NodeEnvelopeNode,
  edges: ReadonlyArray<NodeEnvelopeEdge>,
): UnrecordedNodeReason | null {
  if (node.data?.type === RETRY_SCOPE_NODE_TYPE) return 'retry_scope';
  for (const edge of edges) {
    // Ligação desenhada a partir do ponto "tools" do agente: a ferramenta é o
    // destino.
    if (edge.sourceHandle === AGENT_TOOL_HANDLE && edge.target === node.id) return 'agent_tool';
    // Ligação desenhada a partir da ferramenta até o ponto "tools" do agente:
    // a ferramenta é a origem.
    if (edge.targetHandle === AGENT_TOOL_HANDLE && edge.source === node.id) return 'agent_tool';
  }
  return null;
}
